import axios, { AxiosInstance } from "axios";
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cityProps, notificationsAttributes, proposal_status, ResultBiroProps, stateProps, userProps } from "../../interfaces";
import { setUpConsultation } from "../../services/generalServices";
import { getConsultLabel, getFranchiseLabel, getPersonLabel, getPropertyLabel, getProposalLabel } from "../../utils/labels";
import { useGlobalWebCheckinUser } from "./global";

interface UserInterface extends userProps { }

interface DataProps {
  token: string;
  user: UserInterface;
}

interface credentialsProps {
  email: string;
  password: string;
}

interface ISearchProps {
  search: string;
}

interface ISearchCitiesProps extends ISearchProps {
  state_id?: string;
}

interface ISearchDocumentsTypesProps extends ISearchProps {
  type: "PF" | "PJ" | "property";
}

interface ISearchSignatoriesProps {
  property_id?: string;
  proposal_id?: string;
}

interface IConsultsServicesProps {
  franchise_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  state_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  city_id: (
    props: ISearchCitiesProps
  ) => Promise<{ data: any; formatted: any }>;
  address: (
    props: ISearchProps
  ) => Promise<{ address?: any; city?: cityProps; state?: stateProps }>;
  document_type_id: (
    props: ISearchDocumentsTypesProps
  ) => Promise<{ data: any; formatted: any }>;
  signatories: (props: ISearchSignatoriesProps) => Promise<any>;
  contract_model_id: (
    props: ISearchProps
  ) => Promise<{ data: any; formatted: any }>;
  user_type_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  default_id: (props: ISearchProps) => Promise<{ data: any; formatted: any }>;
  [key: string]: (
    props: any
  ) => Promise<
    | { address?: any; city?: cityProps; state?: stateProps }
    | { data: any; formatted: any }
  >;
}

interface ApiContextData {
  consultsServices: IConsultsServicesProps;
  api: AxiosInstance;
  user: UserInterface;
  token?: string;
  signIn(credentials: credentialsProps, remmemberMe?: boolean): Promise<void>;
  signOut(): void;
  notifications: notificationsAttributes[];
  setNotifications: React.Dispatch<
    React.SetStateAction<notificationsAttributes[]>
  >;
  updateUser(): Promise<void>;
  remmemberMe: boolean;
  markAsRead: (data: any) => Promise<void>;
  setRemmemberMe: React.Dispatch<React.SetStateAction<boolean>>;
  remmemberCredentials: credentialsProps;
}

const ApiContext = createContext<ApiContextData>({} as ApiContextData);

export const ApiProvider: React.FC = ({ children }) => {
  const { notify } = useGlobalWebCheckinUser();

  const [notifications, setNotifications] = useState<notificationsAttributes[]>(
    []
  );
  const [remmemberMe, setRemmemberMe] = useState(() => {
    const rem = localStorage.getItem("@web-checkin:remmember");
    return !!rem;
  });
  const [remmemberCredentials, setRemmemberCredentials] = useState(() => {
    const rem = localStorage.getItem("@web-checkin:remmember");
    return rem ? { ...JSON.parse(rem) } : {};
  });
  const [data, setData] = useState<DataProps>(() => {
    const token = localStorage.getItem("@web-checkin:token");
    const user = localStorage.getItem("@web-checkin:user");
    if (token && user) return { token, user: JSON.parse(user) };
    return {} as DataProps;
  });
  const navigate = useNavigate();

  const api = axios.create({ baseURL: process.env.REACT_APP_API });

  const [params] = useSearchParams();

  api.interceptors.request.use(async (config: any) => {
    const token = localStorage.getItem("@web-checkin:token");
    const bus_id = params.get("bus_id") || localStorage.getItem("@web-checkin:bus_id");
    const url = config.url;
    const query = url.split("?")[1];

    if (!url.includes("bus_id")) {
      if (query) {
        if (url?.split("")?.pop() === "&")
          config.url = `${url}bus_id=${bus_id}`;
        else config.url = `${url}&bus_id=${bus_id}`;
      } else if (url.includes("?"))
        config.url = `${url}bus_id=${bus_id}`;
      else config.url = `${url}?bus_id=${bus_id}`;
    }

    config.headers.authorization = `Bearer ${token}`;

    return config;
  });

  api.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  const searchFranchises = async (props: ISearchProps) => {
    const string = setUpConsultation(props);

    try {
      const result = await api.get(`/generic_searches/franchises?${string}`);
     /*  const formatted = result.data.rows.map((item: any) => ({
        label: getFranchiseLabel({ franchise: item }),
        value: item.id,
        this: item,
      })); */

      const data = result.data.rows;
      return { data, formatted: null };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchState = async (props: ISearchProps) => {
    const string = setUpConsultation(props);

    try {
      const result = await api.get(`/states?${string}`);
      const formatted = result.data.rows.map((item: any) => ({
        label: item.name,
        value: item.id,
        initials: item.initials,
        this: item,
      }));

      const data = result.data.rows;
      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchCities = async (props: ISearchCitiesProps) => {
    const string = setUpConsultation(props);

    try {
      if (!props.state_id) return { data: [], formatted: [] };
      const result = await api.get(`/generic_searches/cities?${string}`);
      const formatted = result.data.rows.map((item: any) => ({
        label: item.name,
        value: item.id,
        this: item,
      }));

      const data = result.data.rows;
      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  };

  const searchAddress = useCallback(async (props: ISearchProps) => {
    const { search } = props;

    try {
      const result = await api.get(`/address/${search}`);

      const address = result.data.address;

      const city: cityProps = result.data.city;

      const state: stateProps = result.data.state;

      return { address, city, state };
    } catch (err) {
      return {};
    }
  }, []);

  const searchDocumentsTypes = useCallback(
    async (props: ISearchDocumentsTypesProps) => {
      const string = setUpConsultation({ ...props, limit: 50 });

      try {
        const result = await api.get(
          `/generic_searches/documents-types?${string}`
        );

        const formatted = result.data.rows.map((item: any) => {
          return { label: item.name, value: item.id, this: item };
        });

        const data = result.data.rows;

        return { data, formatted };
      } catch (err) {
        return { data: [], formatted: [] };
      }
    },
    []
  );

  const searchSignatories = useCallback(
    async (props: ISearchSignatoriesProps) => {
      const { proposal_id, property_id } = props;

      try {
        const _type = proposal_id ? "proposals" : "properties";

        const _id = proposal_id ? proposal_id : property_id;

        const url = `/${_type}/signatories/${_id}`;

        const { data } = await api.get(url);

        return data;
      } catch (error) {
        return [];
      }
    },
    []
  );

  const searchContractModels = useCallback(async (props: ISearchProps) => {
    const string = setUpConsultation({ ...props, limit: 50 });

    try {
      const result = await api.get(
        `/generic_searches/contract-models?${string}`
      );

      const formatted = result.data.rows.map((item: any) => {
        return { label: item.name, value: item.id, this: item };
      });

      const data = result.data.rows;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const searchUserTypes = useCallback(async ({ search }: ISearchProps) => {
    try {
      const result = await api.get("/user-types", { params: { search } });

      const formatted = result.data.data.map((item: any) => {
        return { label: item.name, value: item.id, this: item };
      });

      const data = result.data.data;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const searchAccommodation = useCallback(async ({ search }: ISearchProps) => {
    return { data: [], formatted: [] };
  }, []);

  const searchReservations = useCallback(async ({ search }: ISearchProps) => {
    try {
      const result = await api.get("/check-ins/availables", {
        params: { search },
      });

      const formatted = result.data.data.map((item: any) => {
        return {
          label: item.reservation_id,
          value: item.reservation_id,
          this: item,
        };
      });

      const data = result.data.data;

      return { data, formatted };
    } catch (err) {
      return { data: [], formatted: [] };
    }
  }, []);

  const consultsServices: IConsultsServicesProps = {
    franchise_id: searchFranchises,
    state_id: searchState,
    city_id: searchCities,
    address: searchAddress,
    document_type_id: searchDocumentsTypes,
    signatories: searchSignatories,
    contract_model_id: searchContractModels,
    user_type_id: searchUserTypes,
    stays_id: searchAccommodation,
    reservation_stays_id: searchReservations,
    default_id: async () => {
      return { data: [], formatted: [] };
    },
  };

  const searchNotifications = useCallback(async () => {
    try {
      const result = await api.get("/notifications");
      setNotifications([...result.data]);
    } catch (err) {
      notify("Erro ao buscar notificações", "error");
      console.error(err);
    }
  }, []);

  const markAsRead = useCallback(
    async ({ user_id, property_id, proposal_id, contract_id, type }) => {
      await api.post("/notifications/maskAsRead", {
        user_id,
        property_id,
        proposal_id,
        contract_id,
        type,
      });
      await searchNotifications();
    },
    []
  );

  const signIn = useCallback(
    async ({ email, password }, remmemberMe = false) => {
      const res = await api.post("/session", { email, password });
      const { token, user } = res.data;
      if (user.user_type_id) {
        if (remmemberMe) {
          localStorage.setItem(
            "@web-checkin:remmember",
            JSON.stringify({ email, password })
          );
          setRemmemberCredentials({ email, password });
        } else {
          localStorage.removeItem("@web-checkin:remmember");
          setRemmemberCredentials({ email: "", password: "" });
        }

        localStorage.setItem("@web-checkin:token", token);
        localStorage.setItem("@web-checkin:user", JSON.stringify(user));
        setData({ token, user });
      } else notify("Você não tem permissão para acessar!", "alert");
    },
    []
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("@web-checkin:token");
    localStorage.removeItem("@web-checkin:user");
    localStorage.removeItem("@web-checkin:franchise");
    setData({} as DataProps);
  }, []);

  const updateUser = useCallback(async () => {
    try {
      const result = await api.get("/users/my-infos");
      const new_user = result.data;
      localStorage.setItem("@web-checkin:user", JSON.stringify(new_user));
      setData((atualData) => {
        return { token: atualData.token, user: new_user };
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  return (
    <ApiContext.Provider
      value={{
        signIn,
        markAsRead,
        remmemberCredentials,
        remmemberMe,
        setRemmemberMe,
        updateUser,
        notifications,
        setNotifications,
        signOut,
        user: data.user,
        token: data.token,
        api,
        consultsServices,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export function useApiWebCheckinUser(): ApiContextData {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
}
